import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import * as Fonts from "../../../../components/Fonts";
import { PlusOnlyModal } from "../../../../components/PlusOnlyModal";
import { PlusTag } from "../../../../components/PlusTag";
import { useAppState } from "../../../../contexts/App";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import { hasActiveSubscription } from "../../../../services/api";
import { List } from "./styles";

type Item = {
  key: string;
  title: string;
  description: string;
  active: boolean;
  onClick: () => void;
  /** Opens even on FREE (no PLUS tag): e.g. to turn off what a Keepo that left PLUS still has on */
  openOnFree?: boolean;
};

export const AdvancedConfigs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // PLUS feature a FREE Keepo tried to open: shown in the PLUS-only notice
  const [plusOnlyFeature, setPlusOnlyFeature] = useState<string>();

  const { appState } = useAppState();

  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);

  const tracking = useMemo(
    () => appState.editingSiteTrackingIntegrations || {},
    [appState.editingSiteTrackingIntegrations]
  );

  const isCustomDomainActive = useMemo(() => Boolean(appState.customDomainSettings), [appState.customDomainSettings]);

  // Keepo com senha: on or off, as the Keepo's data came (the password page keeps it up to date)
  const isPasswordOn = !!appState.editingSitePasswordProtected;

  // Verificação de idade: open to every plan
  const isAgeGateOn = !!appState.editingSiteAgeGated;

  const goIfIsPlus = useCallback(
    (featureTitle: string, cb: () => void) => {
      if (!isPlus) {
        setPlusOnlyFeature(featureTitle);
        return;
      }

      if (typeof cb === "function") {
        cb();
      }
    },
    [isPlus]
  );

  // In-app flow (replaces the old Google Form)
  const goCustomDomain = useCallback(() => navigate("/settings/custom-domain"), [navigate]);

  const goTrackingPage = useCallback(
    (path: string) => {
      navigate(`/settings/${path}`);
    },
    [navigate]
  );

  const prefix = "pages.settings.components.advancedConfigs";
  const items: Item[] = [
    {
      key: "custom-domain",
      title: t(`${prefix}.customDomain.title`),
      description: t(`${prefix}.customDomain.description`),
      active: isCustomDomainActive,
      onClick: () => goIfIsPlus(t(`${prefix}.customDomain.title`), goCustomDomain),
    },
    {
      key: "site-password",
      title: t(`${prefix}.sitePassword.title`),
      description: t(`${prefix}.sitePassword.description`),
      active: isPasswordOn,
      // A Keepo that left PLUS with the password on still opens it: to turn it off
      openOnFree: isPasswordOn,
      onClick: () =>
        isPasswordOn
          ? navigate("/settings/password")
          : goIfIsPlus(t(`${prefix}.sitePassword.title`), () => navigate("/settings/password")),
    },
    {
      key: "age-gate",
      title: t(`${prefix}.ageGate.title`),
      description: t(`${prefix}.ageGate.description`),
      active: isAgeGateOn,
      openOnFree: true,
      onClick: () => navigate("/settings/age-gate"),
    },
    {
      key: "facebook-pixel",
      title: t(`${prefix}.tracking.facebookPixel.title`),
      description: t(`${prefix}.tracking.facebookPixel.description`),
      active: !!tracking.facebookPixel,
      onClick: () => goIfIsPlus(t(`${prefix}.tracking.facebookPixel.title`), () => goTrackingPage("facebook-pixel")),
    },
    {
      key: "google-analytics",
      title: t(`${prefix}.tracking.googleAnalytics.title`),
      description: t(`${prefix}.tracking.googleAnalytics.description`),
      active: !!tracking.googleAnalytics,
      onClick: () =>
        goIfIsPlus(t(`${prefix}.tracking.googleAnalytics.title`), () => goTrackingPage("google-analytics")),
    },
    {
      key: "tag-manager",
      title: t(`${prefix}.tracking.tagManager.title`),
      description: t(`${prefix}.tracking.tagManager.description`),
      active: !!tracking.tagManager,
      onClick: () => goIfIsPlus(t(`${prefix}.tracking.tagManager.title`), () => goTrackingPage("tag-manager")),
    },
  ];

  return (
    <>
      <Fonts.Label>{t("pages.settings.components.advancedConfigs.title")}</Fonts.Label>
      <List>
        {items.map(item => (
          <li key={item.key}>
            <button type="button" onClick={item.onClick}>
              <span className="text">
                <span className="title">{item.title}</span>
                <span className="description">{item.description}</span>
              </span>
              {!isPlus && !item.openOnFree && <PlusTag />}
              {(isPlus || item.openOnFree) && item.active && (
                <span className="active">{t("pages.keepoDashboard.active.on")}</span>
              )}
              <span className="chevron">
                <ChevronRightIcon width={12} height={12} color="currentColor" />
              </span>
            </button>
          </li>
        ))}
      </List>
      {plusOnlyFeature && <PlusOnlyModal feature={plusOnlyFeature} onClose={() => setPlusOnlyFeature(undefined)} />}
    </>
  );
};
